import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, downloadFile } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoISO(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function Cashflow() {
  const { currency } = useCurrency();
  const navigate = useNavigate();
  const [from, setFrom] = useState(daysAgoISO(29));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/cashflow/daily?from_date=${from}&to_date=${to}&currency=${currency}`
      );
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load cashflow");
    } finally {
      setLoading(false);
    }
  }, [from, to, currency]);

  useEffect(() => {
    load();
  }, [load]);

  const exportXlsx = async () => {
    try {
      await downloadFile(
        `/cashflow/export?from_date=${from}&to_date=${to}&currency=${currency}`,
        `cashflow_${from}_to_${to}.xlsx`
      );
    } catch {
      toast.error("Export failed");
    }
  };

  const chartData = data ? [...data.days].reverse().map((d) => ({ ...d })) : [];

  return (
    <div className="space-y-5" data-testid="cashflow-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Cashflow</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Daily cash closing per bank account, built from actual bank lines.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="cashflow-from"
            className="h-10 rounded-md bg-background border border-input px-2 text-xs"
          />
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="cashflow-to"
            className="h-10 rounded-md bg-background border border-input px-2 text-xs"
          />
          <Button variant="outline" size="sm" onClick={exportXlsx} className="gap-2 h-10" data-testid="cashflow-export">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-24 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Closing cash", formatMoney(data.totals.closing, currency), "cashflow-kpi-closing"],
              ["Inflow", formatMoney(data.totals.inflow, currency), "cashflow-kpi-inflow"],
              ["Outflow", formatMoney(data.totals.outflow, currency), "cashflow-kpi-outflow"],
              ["Net movement", formatMoney(data.totals.net, currency), "cashflow-kpi-net"],
            ].map(([label, value, tid]) => (
              <Card key={tid} className="p-4" data-testid={tid}>
                <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
                <p className="font-heading text-xl mt-1.5 font-mono">{value}</p>
              </Card>
            ))}
          </div>

          <Card className="p-4">
            <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-3">
              Closing cash trend
            </p>
            <div className="h-[220px]" data-testid="cashflow-chart">
              <ResponsiveContainer width="100%" height="100%" minHeight={180}>
                <AreaChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="cashGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "var(--chart-axis)" }} />
                  <YAxis tick={{ fontSize: 10, fill: "var(--chart-axis)" }} width={70} />
                  <Tooltip
                    contentStyle={{
                      background: "var(--tooltip-bg)",
                      border: "1px solid var(--hairline)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    formatter={(v) => formatMoney(v, currency)}
                  />
                  <Area
                    type="monotone"
                    dataKey="closing"
                    stroke="#22d3ee"
                    strokeWidth={2}
                    fill="url(#cashGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <Card className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cashflow-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-white/5">
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-right px-4 py-3">Opening</th>
                  <th className="text-right px-4 py-3">Inflow</th>
                  <th className="text-right px-4 py-3">Outflow</th>
                  <th className="text-right px-4 py-3">Closing</th>
                  {data.accounts.map((a) => (
                    <th key={a.id} className="text-right px-4 py-3 whitespace-nowrap">
                      {a.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.days.map((d) => (
                  <tr
                    key={d.date}
                    className="border-b border-white/[0.03] hover:bg-white/[0.02]"
                    data-testid={`cashflow-row-${d.date}`}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs">
                      <button
                        onClick={() => navigate(`/recon?status=all&from=${d.date}&to=${d.date}`)}
                        data-testid={`cashflow-day-link-${d.date}`}
                        title="Open this day's bank lines"
                        className="hover:text-brand transition-colors underline-offset-4 hover:underline"
                      >
                        {d.date}
                      </button>
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-muted-foreground">
                      {formatMoney(d.opening, currency, { compact: true })}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-emerald-300">
                      {d.inflow ? formatMoney(d.inflow, currency, { compact: true }) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-rose-300">
                      {d.outflow ? formatMoney(d.outflow, currency, { compact: true }) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs">
                      {formatMoney(d.closing, currency, { compact: true })}
                    </td>
                    {data.accounts.map((a) => (
                      <td key={a.id} className="px-4 py-2.5 text-right font-mono text-xs text-muted-foreground">
                        {formatMoney(d.accounts[a.id]?.closing || 0, currency, { compact: true })}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}
